import { t, type Lang } from '../i18n.js';

/**
 * Which drawing this is, and how to work on a different one.
 *
 * Two findings from the journey pass close here, and they close together
 * because they are the same omission seen twice.
 *
 * **The surface never named the drawing.** It carried one through five
 * diagnostics, a scale decision and a font choice without ever saying which
 * drawing it was. On a second monitor beside a CAD window holding a different
 * revision, that is the first question a reviewer has.
 *
 * **Start over was the last element of the evidence column**, under five folds,
 * in a column deliberately ordered "what you get, what was read, how it was
 * read" — so it sat exactly where somebody lands after opening a fold to inspect
 * something. Moving it here is not only about getting it away from there: an
 * action that changes *which drawing* belongs beside the name of the drawing it
 * would change, at the same scope, and nowhere near the zoom controls that
 * change only the view.
 *
 * It is not called "Start over" any more either. That named the software's
 * state; this names what happens.
 */
export function DrawingBar({
  name,
  lang,
  label = 'loadAnother',
  onLoadAnother,
}: {
  name: string | null;
  lang: Lang;
  /**
   * Which action this is. The comparison surface needs two files rather than
   * one, so there is no picker to open and its wording has to say so.
   */
  label?: 'loadAnother' | 'comparePairAgain';
  onLoadAnother: () => void;
}): JSX.Element {
  return (
    <>
      {name !== null && (
        <span className="drawing-name" title={name}>
          {name}
        </span>
      )}
      <button type="button" className="load-another" onClick={onLoadAnother}>
        {t(label, lang)}
      </button>
    </>
  );
}
